import {
  Box,
  Button,
  Flex,
  Link as ChakraLink,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { MdChevronRight, MdSchedule } from "react-icons/md";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { getUpcomingOfficeHoursRequest } from "../../modules/students-modules/office-hours/Actions";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
};

function formatRange(fromVal, toVal) {
  const a = fromVal ? dayjs(fromVal) : null;
  const b = toVal ? dayjs(toVal) : null;
  if (a?.isValid() && b?.isValid()) {
    return `${a.format("D MMM YYYY, HH:mm")} – ${b.format("HH:mm")}`;
  }
  if (a?.isValid()) return a.format("D MMM YYYY, HH:mm");
  return "—";
}

function useSecondsUntil(iso) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    void tick;
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((t - Date.now()) / 1000);
  }, [iso, tick]);
}

function CountdownLabel({ fromIso }) {
  const sec = useSecondsUntil(fromIso);
  if (sec == null) return null;
  if (sec <= 0) {
    return (
      <Text fontSize="xs" fontWeight="700" color={C.secondary} whiteSpace="nowrap">
        Started
      </Text>
    );
  }
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0 || d > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  parts.push(`${s}s`);
  return (
    <Text
      fontSize="xs"
      fontWeight="800"
      color={C.primary}
      fontVariantNumeric="tabular-nums"
      whiteSpace="nowrap"
      sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
    >
      {parts.join(" ")}
    </Text>
  );
}

const UpcomingOfficeHoursPanel = () => {
  const dispatch = useDispatch();
  const userData = useSelector((s) => s.user.userData);
  const upcoming = useSelector((s) => s.studentOfficeHours.upcoming);
  const isLoading = useSelector((s) => s.studentOfficeHours.isLoadingUpcoming);
  const studentId = userData?.id;

  useEffect(() => {
    if (studentId != null) dispatch(getUpcomingOfficeHoursRequest(studentId));
  }, [dispatch, studentId]);

  const items = useMemo(() => {
    const list = Array.isArray(upcoming) ? upcoming : [];
    return list.slice(0, 3);
  }, [upcoming]);

  return (
    <Box
      p={{ base: 5, md: 6 }}
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      boxShadow="sm"
      w="full"
      minW={0}
      h="full"
      display="flex"
      flexDirection="column"
      maxH={{ base: "360px", md: "420px" }}
    >
      <Flex align="center" justify="space-between" gap={3} mb={4}>
        <Box minW={0}>
          <Text
            fontSize="sm"
            fontWeight="800"
            letterSpacing="0.08em"
            color={C.primary}
            textTransform="uppercase"
            mb={0.5}
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          >
            One to one support
          </Text>
          <Text fontSize="xs" fontWeight="500" color={C.muted}>
            Upcoming sessions
          </Text>
        </Box>
        <ChakraLink
          as={Link}
          to="/students/office-hours"
          fontSize="9px"
          fontWeight="800"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color={C.secondary}
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          flexShrink={0}
          _hover={{ color: C.primary, textDecoration: "none" }}
        >
          View all
          <MdChevronRight size={16} />
        </ChakraLink>
      </Flex>

      {isLoading ? (
        <VStack align="stretch" spacing={3} flex="1" minH={0}>
          <Skeleton height="72px" borderRadius="md" />
          <Skeleton height="72px" borderRadius="md" />
        </VStack>
      ) : items.length === 0 ? (
        <Text fontSize="sm" color={C.muted} lineHeight="tall">
          No upcoming one to one sessions yet. Browse open slots to join a session.
        </Text>
      ) : (
        <Box flex="1" minH={0} overflowY="auto">
        <VStack align="stretch" spacing={3}>
          {items.map((item) => {
            const title = item?.title || item?.name || "Office hour";
            const zoom = item?.zoom_link;
            const assistantName = item?.assistant?.name;
            return (
              <Flex
                key={item.id ?? `up-${item.from}`}
                align="flex-start"
                gap={3}
                p={3}
                bg={studentSurfaces.muted}
                borderWidth="1px"
                borderColor={C.border}
                borderRadius="md"
                flexWrap="wrap"
              >
                <Box
                  color={C.primary}
                  flexShrink={0}
                  mt={0.5}
                  sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                >
                  <MdSchedule size={20} />
                </Box>
                <Box flex="1" minW={0}>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color={C.secondary}
                    noOfLines={2}
                    sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                  >
                    {title}
                  </Text>
                  <Text fontSize="xs" color={C.muted} fontWeight="600" mt={0.5}>
                    {formatRange(item?.from, item?.to)}
                  </Text>
                  {assistantName ? (
                    <Text fontSize="xs" color={C.muted} fontWeight="600" mt={0.5} noOfLines={1}>
                      Assistant: {assistantName}
                    </Text>
                  ) : null}
                </Box>
                <VStack align="flex-end" spacing={2} flexShrink={0}>
                  <CountdownLabel fromIso={item?.from} />
                  {zoom ? (
                    <Button
                      as="a"
                      href={zoom}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      bg={C.primary}
                      color="white"
                      fontWeight="800"
                      _hover={{ bg: C.secondary }}
                    >
                      Join
                    </Button>
                  ) : null}
                </VStack>
              </Flex>
            );
          })}
        </VStack>
        </Box>
      )}

      {!isLoading && items.length === 0 ? (
        <Button
          as={Link}
          to="/students/office-hours"
          mt={4}
          w="full"
          size="sm"
          bg={C.primary}
          color={studentPalette.cream}
          fontWeight="800"
          textTransform="uppercase"
          fontSize="10px"
          letterSpacing="0.08em"
          borderRadius="lg"
          _hover={{ bg: C.secondary }}
        >
          Browse one to one support
        </Button>
      ) : null}
    </Box>
  );
};

export default UpcomingOfficeHoursPanel;
